import { API_BASE } from '../../config/apiBase';
import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Separator } from '../../components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  ArrowLeft,
  FileText,
  Download,
  Truck,
  Building2,
  Calculator,
  DollarSign,
  Users,
  CheckCircle,
  AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const API = API_BASE;

const SummaryApp = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const clientId = searchParams.get('client');
  
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(!!id);
  const [saving, setSaving] = useState(false);
  const [clients, setClients] = useState([]);
  const [appraisals, setAppraisals] = useState([]);
  const [bankAnalyses, setBankAnalyses] = useState([]);
  const [creditEvals, setCreditEvals] = useState([]);
  const [dealStructures, setDealStructures] = useState([]);
  
  const [selectedClient, setSelectedClient] = useState(clientId || undefined);
  const [selectedAppraisal, setSelectedAppraisal] = useState(undefined);
  const [selectedBankAnalysis, setSelectedBankAnalysis] = useState(undefined);
  const [selectedCreditEval, setSelectedCreditEval] = useState(undefined);
  const [selectedDeal, setSelectedDeal] = useState(undefined);
  const [recommendation, setRecommendation] = useState('REVIEW');
  const [internalNotes, setInternalNotes] = useState(undefined);

  useEffect(() => {
    fetchInitialData();
    if (id && id !== 'new') {
      fetchSummary();
    } else {
      setLoading(false);
    }
  }, [id]);

  const fetchInitialData = async () => {
    try {
      const [clientsRes, appraisalsRes, bankRes, evalsRes, dealsRes] = await Promise.all([
        axios.get(`${API}/clients`),
        axios.get(`${API}/appraisals`),
        axios.get(`${API}/bank-analyses`),
        axios.get(`${API}/credit-evaluations`),
        axios.get(`${API}/deal-structures`)
      ]);
      setClients(clientsRes.data);
      setAppraisals(appraisalsRes.data);
      setBankAnalyses(bankRes.data);
      setCreditEvals(evalsRes.data);
      setDealStructures(dealsRes.data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchSummary = async () => {
    try {
      const response = await axios.get(`${API}/credit-summaries/${id}`);
      setSummary(response.data);
      setSelectedClient(response.data.client_id || undefined);
      setRecommendation(response.data.recommendation || 'REVIEW');
      setInternalNotes(response.data.internal_notes || '');
    } catch (error) {
      toast.error('Error al cargar el resumen');
      navigate('/apps/summary');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!recommendation) {
      toast.error('La recomendación es requerida');
      return;
    }
    
    setSaving(true);
    try {
      const response = await axios.post(`${API}/credit-summaries`, {
        client_id: selectedClient || null,
        appraisal_id: selectedAppraisal || null,
        bank_analysis_id: selectedBankAnalysis || null,
        credit_evaluation_id: selectedCreditEval || null,
        deal_structure_id: selectedDeal || null,
        recommendation,
        internal_notes: internalNotes
      });
      toast.success('Resumen de crédito creado');
      navigate(`/apps/summary/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear resumen');
    } finally {
      setSaving(false);
    }
  };

  const generatePDF = () => {
    if (!summary) return;
    
    const doc = new jsPDF();
    
    // Header
    doc.setFillColor(15, 23, 42);
    doc.rect(0, 0, 220, 35, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('INSPIRED FUNDING', 20, 15);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.text('Credit Analysis Summary', 20, 23);
    doc.text(`ID: ${summary.id.substring(0, 8)}`, 20, 30);
    
    doc.setTextColor(0, 0, 0);
    let y = 45;
    
    // Client Info
    if (summary.client_info) {
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Información del Cliente', 20, y);
      y += 7;
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Nombre: ${summary.client_info.full_name || 'N/A'}`, 25, y); y += 5;
      doc.text(`Email: ${summary.client_info.email || 'N/A'}`, 25, y); y += 5;
      doc.text(`Teléfono: ${summary.client_info.phone || 'N/A'}`, 25, y); y += 5;
      doc.text(`CDL: ${summary.client_info.has_cdl ? 'Sí' : 'No'}`, 25, y); y += 10;
    }
    
    // Appraisal Info
    if (summary.appraisal_info) {
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Tasación del Equipo', 20, y);
      y += 7;
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Equipo: ${summary.appraisal_info.asset?.year} ${summary.appraisal_info.asset?.make} ${summary.appraisal_info.asset?.model}`, 25, y); y += 5;
      doc.text(`Millaje: ${summary.appraisal_info.asset?.mileage?.toLocaleString() || 'N/A'} mi`, 25, y); y += 5;
      doc.text(`Precio Solicitado: $${summary.appraisal_info.asset?.requested_price?.toLocaleString() || 'N/A'}`, 25, y); y += 5;
      doc.text(`Valor Tasado: $${summary.appraisal_info.appraised_value?.toLocaleString() || 'N/A'}`, 25, y); y += 10;
    }
    
    // Bank Analysis
    if (summary.bank_analysis_info) {
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Análisis Bancario', 20, y);
      y += 7;
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Ingreso Mensual Prom: $${summary.bank_analysis_info.avg_monthly_income?.toLocaleString() || 0}`, 25, y); y += 5;
      doc.text(`Balance Final Prom: $${summary.bank_analysis_info.avg_ending_balance?.toLocaleString() || 0}`, 25, y); y += 5;
      doc.text(`Depósito Mínimo: $${summary.bank_analysis_info.min_monthly_deposit?.toLocaleString() || 0}`, 25, y); y += 5;
      doc.text(`Balance Mínimo: $${summary.bank_analysis_info.min_ending_balance?.toLocaleString() || 0}`, 25, y); y += 10;
    }
    
    // Credit Evaluation
    if (summary.credit_evaluation_info) {
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Evaluación de Crédito', 20, y);
      y += 7;
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Tier Asignado: ${summary.credit_evaluation_info.assigned_tier || 'Sin tier'}`, 25, y); y += 5;
      if (summary.credit_evaluation_info.tier_override) {
        doc.text(`Override por: ${summary.credit_evaluation_info.tier_override_by}`, 25, y); y += 5;
      }
      y += 5;
    }
    
    // Deal Structure
    if (summary.deal_structure_info) {
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Estructura del Deal', 20, y);
      y += 7;
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Monto Financiado: $${summary.deal_structure_info.amount_financed?.toLocaleString()}`, 25, y); y += 5;
      doc.text(`Enganche: $${summary.deal_structure_info.down_payment?.toLocaleString()} (${summary.deal_structure_info.down_payment_pct}%)`, 25, y); y += 5;
      doc.text(`Plazo: ${summary.deal_structure_info.term_months} meses`, 25, y); y += 5;
      doc.text(`Tasa: ${summary.deal_structure_info.interest_rate}%`, 25, y); y += 5;
      doc.text(`Pago Mensual: $${summary.deal_structure_info.monthly_payment?.toLocaleString()}`, 25, y); y += 5;
      doc.text(`LTV: ${summary.deal_structure_info.ltv?.toFixed(1)}%`, 25, y); y += 10;
    }
    
    // Recommendation
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Recomendación del Sistema', 20, y);
    y += 7;
    
    const rec = summary.recommendation;
    if (rec === 'APPROVE') {
      doc.setTextColor(5, 150, 105);
    } else if (rec === 'DECLINE') {
      doc.setTextColor(220, 38, 38);
    } else {
      doc.setTextColor(245, 158, 11);
    }
    
    doc.setFontSize(12);
    doc.text(rec, 25, y);
    
    // Internal Notes
    if (summary.internal_notes) {
      doc.setTextColor(0, 0, 0);
      y += 10;
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Notas Internas', 20, y);
      y += 7;
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      const lines = doc.splitTextToSize(summary.internal_notes, 170);
      doc.text(lines, 25, y);
    }
    
    // Footer
    doc.setFontSize(8);
    doc.setTextColor(128, 128, 128);
    doc.text(`Generado: ${new Date().toLocaleString()}`, 20, 285);
    doc.text('Inspired Funding LLC - Solo Uso Interno', 120, 285);
    
    doc.save(`credit-summary-${summary.id.substring(0, 8)}.pdf`);
    toast.success('PDF generado');
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  // New summary form
  if (!id || id === 'new') {
    return (
      <div className="space-y-6" data-testid="new-summary-page">
        <Button variant="ghost" onClick={() => navigate('/apps/summary')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Volver a Resúmenes
        </Button>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-red-600" />
              Nuevo Resumen de Crédito
            </CardTitle>
            <CardDescription>Consolida la información de todas las apps</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Data Sources */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Cliente</Label>
                <Select value={selectedClient} onValueChange={setSelectedClient}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin cliente</SelectItem>
                    {clients.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.full_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Tasación</Label>
                <Select value={selectedAppraisal} onValueChange={setSelectedAppraisal}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Ninguna</SelectItem>
                    {appraisals.filter(a => a.status === 'completed').map((a) => (
                      <SelectItem key={a.id} value={a.id}>
                        {a.asset?.year} {a.asset?.make} - ${a.appraised_value?.toLocaleString()}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Análisis Bancario</Label>
                <Select value={selectedBankAnalysis} onValueChange={setSelectedBankAnalysis}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Ninguno</SelectItem>
                    {bankAnalyses.filter(a => a.status === 'completed').map((a) => (
                      <SelectItem key={a.id} value={a.id}>
                        {a.client_name || 'Sin cliente'} - ${a.avg_monthly_income?.toLocaleString()}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Evaluación de Crédito</Label>
                <Select value={selectedCreditEval} onValueChange={setSelectedCreditEval}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Ninguna</SelectItem>
                    {creditEvals.map((e) => (
                      <SelectItem key={e.id} value={e.id}>
                        {e.client_name || 'Sin cliente'} - Tier {e.assigned_tier || 'N/A'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Deal Structure</Label>
                <Select value={selectedDeal} onValueChange={setSelectedDeal}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Ninguno</SelectItem>
                    {dealStructures.map((d) => (
                      <SelectItem key={d.id} value={d.id}>
                        {d.client_name || 'Sin cliente'} - ${d.amount_financed?.toLocaleString()}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Separator />

            {/* Recommendation */}
            <div className="space-y-2">
              <Label>Recomendación *</Label>
              <Select value={recommendation} onValueChange={setRecommendation}>
                <SelectTrigger className="w-full md:w-64">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="APPROVE">✅ APPROVE - Aprobar</SelectItem>
                  <SelectItem value="REVIEW">⚠️ REVIEW - Revisar</SelectItem>
                  <SelectItem value="DECLINE">❌ DECLINE - Declinar</SelectItem>
                  <SelectItem value="CONDITIONAL">📝 CONDITIONAL - Condicional</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Notas Internas</Label>
              <Textarea
                value={internalNotes}
                onChange={(e) => setInternalNotes(e.target.value)}
                placeholder="Notas y comentarios internos sobre el caso..."
                rows={4}
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => navigate('/apps/summary')}>Cancelar</Button>
              <Button onClick={handleCreate} disabled={saving}>
                {saving ? 'Creando...' : 'Crear Resumen'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Existing summary view
  return (
    <div className="space-y-6" data-testid="summary-detail-page">
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={() => navigate('/apps/summary')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Volver a Resúmenes
        </Button>
        <Button onClick={generatePDF}>
          <Download className="w-4 h-4 mr-2" />
          Exportar PDF
        </Button>
      </div>

      {/* Header Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-red-600" />
                Resumen de Crédito
              </CardTitle>
              <CardDescription>
                {summary?.client_info?.full_name || 'Sin cliente asociado'}
              </CardDescription>
            </div>
            <Badge 
              variant={summary?.recommendation === 'APPROVE' ? 'default' : summary?.recommendation === 'DECLINE' ? 'destructive' : 'secondary'}
              className="text-lg py-1 px-4"
            >
              {summary?.recommendation}
            </Badge>
          </div>
        </CardHeader>
      </Card>

      {/* Consolidated Data */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Client Info */}
        {summary?.client_info && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Users className="w-4 h-4" /> Cliente
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Nombre:</span>
                <span className="font-medium">{summary.client_info.full_name}</span>
              </div>
              {summary.client_info.email && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Email:</span>
                  <span>{summary.client_info.email}</span>
                </div>
              )}
              {summary.client_info.phone && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Teléfono:</span>
                  <span>{summary.client_info.phone}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-muted-foreground">CDL:</span>
                <span>{summary.client_info.has_cdl ? 'Sí' : 'No'}</span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Appraisal */}
        {summary?.appraisal_info && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Truck className="w-4 h-4" /> Tasación
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Equipo:</span>
                <span className="font-medium">
                  {summary.appraisal_info.asset?.year} {summary.appraisal_info.asset?.make} {summary.appraisal_info.asset?.model}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Millaje:</span>
                <span>{summary.appraisal_info.asset?.mileage?.toLocaleString()} mi</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Valor Tasado:</span>
                <span className="font-medium text-blue-600">${summary.appraisal_info.appraised_value?.toLocaleString()}</span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Bank Analysis */}
        {summary?.bank_analysis_info && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Building2 className="w-4 h-4" /> Análisis Bancario
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Ingreso Prom:</span>
                <span className="font-medium text-emerald-600">${summary.bank_analysis_info.avg_monthly_income?.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Balance Prom:</span>
                <span>${summary.bank_analysis_info.avg_ending_balance?.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Depósito Mín:</span>
                <span>${summary.bank_analysis_info.min_monthly_deposit?.toLocaleString()}</span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Credit Evaluation */}
        {summary?.credit_evaluation_info && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Calculator className="w-4 h-4" /> Evaluación de Crédito
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Tier Asignado:</span>
                <Badge variant="default">Tier {summary.credit_evaluation_info.assigned_tier || 'N/A'}</Badge>
              </div>
              {summary.credit_evaluation_info.tier_override && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Override:</span>
                  <span className="text-amber-600">{summary.credit_evaluation_info.tier_override_by}</span>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Deal Structure */}
        {summary?.deal_structure_info && (
          <Card className="md:col-span-2">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <DollarSign className="w-4 h-4" /> Estructura del Deal
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Financiado</p>
                  <p className="font-medium text-lg">${summary.deal_structure_info.amount_financed?.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Enganche</p>
                  <p className="font-medium">${summary.deal_structure_info.down_payment?.toLocaleString()} ({summary.deal_structure_info.down_payment_pct}%)</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Pago Mensual</p>
                  <p className="font-medium text-lg text-emerald-600">${summary.deal_structure_info.monthly_payment?.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">LTV</p>
                  <p className="font-medium">{summary.deal_structure_info.ltv?.toFixed(1)}%</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Internal Notes */}
      {summary?.internal_notes && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Notas Internas</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm whitespace-pre-wrap">{summary.internal_notes}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SummaryApp;
